import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box, Card, Stack, Typography, Grid, Button, TextField, MenuItem, IconButton,
  Accordion, AccordionSummary, AccordionDetails, Chip, Divider, Tooltip,
  Table, TableHead, TableRow, TableCell, TableBody, TableContainer,
  Dialog, DialogTitle, DialogContent, DialogActions, Checkbox, FormControlLabel,
  ToggleButtonGroup, ToggleButton, Switch, Autocomplete,
  CircularProgress, LinearProgress, Alert, Avatar,
} from '@mui/material';
import {
  Tune as SettingsIcon, Save as SaveIcon,
  Add as AddIcon, Edit as EditIcon, Delete as DeleteIcon,
  ExpandMore as ExpandIcon,
  CalendarMonth as PeriodIcon, EventAvailable as TermIcon,
  Calculate as WeightIcon, Grade as GradeIcon,
  PlayArrow as ActivateIcon, AutoAwesome as SeedIcon,
  AssignmentInd as TeacherAssignIcon,
  Notes as RemarksIcon,
  EmojiEvents as MeritIcon,
  Preview as PreviewIcon, Refresh as RefreshIcon, Launch as LaunchIcon,
} from '@mui/icons-material';
import ModuleHeader from '../../components/ModuleHeader';
import API from '../../BaseURL';
import swal from '../../utils/swal';
import TeacherAssignmentsPanel from './TeacherAssignmentsPanel';

const blankBand = { marks_1: 0, marks_2: 0, grade: '', remarks: '' };
const blankSba  = { label: '', item_count: 1, weight_pct: 0, max_mark_per_item: 100, is_exam: false, sort_order: 0 };

// Remarks catalogue — DB categories are kept as-is so backend routes,
// report cards and the per-student sheet continue to look them up by the
// same `cat` values, but the UI displays friendlier labels.
const REMARK_CATEGORIES = [
  { cat: 'Conduct',           label: 'Conduct' },
  { cat: 'Attitude',          label: 'Attitude' },
  { cat: 'Teachers Remark',   label: 'Remarks — Class Teacher' },
  { cat: 'Headmasters Remark',label: 'Remarks — Headmaster' },
];
const blankRemark = { cat: REMARK_CATEGORIES[0].cat, name: '' };

// Compact-table styling — every settings table uses this so they all
// feel like one consistent data grid: tight rows, small uppercase
// headers, hairline borders, hover row, no wasted whitespace.
const COMPACT_TABLE = {
  '& .MuiTableHead-root .MuiTableCell-root': {
    fontWeight: 700,
    fontSize: 10.5,
    letterSpacing: 0.4,
    textTransform: 'uppercase',
    color: 'text.secondary',
    bgcolor: 'grey.50',
    py: 0.7,
    px: 1.25,
    lineHeight: 1.2,
    borderBottom: '1px solid',
    borderColor: 'divider',
  },
  '& .MuiTableBody-root .MuiTableCell-root': {
    fontSize: 12.5,
    py: 0.55,
    px: 1.25,
    borderBottom: '1px solid',
    borderColor: 'rgba(0,0,0,0.04)',
  },
  '& .MuiTableBody-root .MuiTableRow-root:last-of-type .MuiTableCell-root': {
    borderBottom: 0,
  },
  '& .MuiIconButton-root': { p: 0.5 },
};

// Smaller chips for inside tables (default MUI small is still tall).
const COMPACT_CHIP = { height: 19, fontSize: 10.5, fontWeight: 700,
  '& .MuiChip-label': { px: 0.8 } };

// Section header used inside each Accordion summary — keeps the icon,
// title, subtitle and a small "what's currently set" preview consistent
// across all four sections.
function SectionTitle({ icon, color = 'primary', title, subtitle, preview }) {
  return (
    <Stack direction="row" alignItems="center" spacing={1.5} sx={{ width: '100%', pr: 1 }}>
      <Box sx={{
        width: 38, height: 38, borderRadius: 1.5, flexShrink: 0,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        bgcolor: `${color}.50`, color: `${color}.main`,
      }}>{icon}</Box>
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography fontWeight={800} sx={{ fontSize: 15, lineHeight: 1.2 }}>{title}</Typography>
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', lineHeight: 1.3 }}>
          {subtitle}
        </Typography>
      </Box>
      {preview && (
        <Box sx={{ display: { xs: 'none', md: 'block' } }}>{preview}</Box>
      )}
    </Stack>
  );
}

// ── Sub-components ────────────────────────────────────────────
// Profile editor body — shared between "expand a profile" and "new
// profile" forms. Pure UI, no I/O: parent owns the draft state and
// the save action.
function ProfileEditor({ draft, setDraft, classOptions, draftSbaTotal, draftHasExam,
                        onOpenSba, onRemoveSba, onSeedGhana }) {
  if (!draft) return null;
  const isDefault = !!draft.is_default;
  const setMode = (v) => setDraft(p => ({ ...p, mode: v }));
  return (
    <Card variant="outlined" sx={{ p: 2, bgcolor: 'grey.50', borderRadius: 2 }}>
      <Grid container spacing={1.5}>
        <Grid item xs={12} sm={isDefault ? 12 : 6}>
          <TextField fullWidth size="small" label="Profile name"
            value={draft.name}
            onChange={e => setDraft(p => ({ ...p, name: e.target.value }))}
            disabled={isDefault}
            helperText={isDefault ? 'The default profile name is fixed' : 'Friendly label, e.g. "JHS SBA"'} />
        </Grid>
        {!isDefault && (
          <Grid item xs={12} sm={6}>
            <TextField select fullWidth size="small" label="Applies to classes"
              SelectProps={{
                multiple: true,
                renderValue: (s) => s.length === 0 ? 'None yet' : s.join(', '),
              }}
              value={draft.classes || []}
              onChange={e => setDraft(p => ({ ...p, classes:
                typeof e.target.value === 'string' ? e.target.value.split(',') : e.target.value }))}
              helperText="Pick any combination — these classes use this profile">
              {classOptions.map(c => (
                <MenuItem key={c} value={c}>
                  <Checkbox size="small" checked={(draft.classes || []).includes(c)} />
                  <Typography sx={{ fontSize: 13 }}>{c}</Typography>
                </MenuItem>
              ))}
              {classOptions.length === 0 && (
                <MenuItem disabled><em>No unassigned classes left</em></MenuItem>
              )}
            </TextField>
          </Grid>
        )}

        <Grid item xs={12}>
          <Typography variant="caption" color="text.secondary"
            sx={{ display: 'block', fontWeight: 800, letterSpacing: 0.5, textTransform: 'uppercase', mb: 0.7 }}>
            Assessment Model
          </Typography>
          <ToggleButtonGroup exclusive value={draft.mode} fullWidth
            onChange={(_, v) => v && setMode(v)}>
            <ToggleButton value="simple" sx={{ textTransform: 'none', fontWeight: 700, py: 1 }}>
              <Box sx={{ textAlign: 'left', width: '100%' }}>
                <Typography fontWeight={800} sx={{ fontSize: 13.5 }}>Simple</Typography>
                <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                  Class % + Exam %. Two numbers.
                </Typography>
              </Box>
            </ToggleButton>
            <ToggleButton value="sba" sx={{ textTransform: 'none', fontWeight: 700, py: 1 }}>
              <Box sx={{ textAlign: 'left', width: '100%' }}>
                <Typography fontWeight={800} sx={{ fontSize: 13.5 }}>SBA</Typography>
                <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                  Assignment / Project / Quiz / Exam.
                </Typography>
              </Box>
            </ToggleButton>
          </ToggleButtonGroup>
        </Grid>

        {draft.mode === 'simple' ? (
          <>
            <Grid item xs={6} sm={3}>
              <TextField fullWidth size="small" type="number" label="Class weight (%)"
                value={draft.cl_sc}
                onChange={e => setDraft(p => ({ ...p, cl_sc: e.target.value }))} />
            </Grid>
            <Grid item xs={6} sm={3}>
              <TextField fullWidth size="small" type="number" label="Exam weight (%)"
                value={draft.exa_sc}
                onChange={e => setDraft(p => ({ ...p, exa_sc: e.target.value }))} />
            </Grid>
            <Grid item xs={6} sm={3}>
              <TextField fullWidth size="small" type="number" label="Class out of"
                value={draft.cl_res}
                onChange={e => setDraft(p => ({ ...p, cl_res: e.target.value }))} />
            </Grid>
            <Grid item xs={6} sm={3}>
              <TextField fullWidth size="small" type="number" label="Exam out of"
                value={draft.ex_res}
                onChange={e => setDraft(p => ({ ...p, ex_res: e.target.value }))} />
            </Grid>
          </>
        ) : (
          <Grid item xs={12}>
            <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1, flexWrap: 'wrap', gap: 1 }}>
              <Stack direction="row" spacing={1} alignItems="center">
                <Chip color={draftSbaTotal === 100 ? 'success' : 'warning'} sx={{ ...COMPACT_CHIP, height: 22 }}
                  label={`Total ${draftSbaTotal}%${draftSbaTotal === 100 ? '' : ' (should be 100)'}`} />
                {!draftHasExam && (draft.components || []).length > 0 && (
                  <Chip color="warning" sx={{ ...COMPACT_CHIP, height: 22 }} label="No End-of-Term Exam row" />
                )}
              </Stack>
              <Stack direction="row" spacing={1}>
                {(draft.components || []).length === 0 && (
                  <Button variant="outlined" size="small" color="primary" startIcon={<SeedIcon />}
                    onClick={onSeedGhana}
                    sx={{ textTransform: 'none', fontWeight: 800 }}>
                    Ghana defaults
                  </Button>
                )}
                <Button variant="contained" color="secondary" size="small" startIcon={<AddIcon />}
                  onClick={() => onOpenSba(null)}
                  sx={{ textTransform: 'none', fontWeight: 800 }}>
                  Add component
                </Button>
              </Stack>
            </Stack>
            <TableContainer sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
              <Table size="small" sx={COMPACT_TABLE}>
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ width: 40 }}>#</TableCell>
                    <TableCell>Component</TableCell>
                    <TableCell align="right" sx={{ width: 80 }}>Items</TableCell>
                    <TableCell align="right" sx={{ width: 80 }}>Max/item</TableCell>
                    <TableCell align="right" sx={{ width: 80 }}>Weight</TableCell>
                    <TableCell sx={{ width: 110 }}>Role</TableCell>
                    <TableCell align="right" sx={{ width: 70 }} />
                  </TableRow>
                </TableHead>
                <TableBody>
                  {(draft.components || []).length === 0 && (
                    <TableRow>
                      <TableCell colSpan={7} align="center"
                        sx={{ py: 2.5, color: 'text.secondary', fontStyle: 'italic' }}>
                        No components yet — click <b>Add component</b> or <b>Ghana defaults</b>.
                      </TableCell>
                    </TableRow>
                  )}
                  {(draft.components || []).map((row, i) => (
                    <TableRow key={i} hover>
                      <TableCell sx={{ color: 'text.secondary' }}>{i + 1}</TableCell>
                      <TableCell sx={{ fontWeight: 700 }}>{row.label}</TableCell>
                      <TableCell align="right">{row.item_count}</TableCell>
                      <TableCell align="right">{Number(row.max_mark_per_item ?? 100)}</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 700 }}>{Number(row.weight_pct).toFixed(1)}%</TableCell>
                      <TableCell>
                        {row.is_exam
                          ? <Chip color="warning" label="Exam" sx={COMPACT_CHIP} />
                          : <Chip variant="outlined" label="Continuous" sx={COMPACT_CHIP} />}
                      </TableCell>
                      <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
                        <Tooltip title="Edit"><IconButton onClick={() => onOpenSba(i)}><EditIcon sx={{ fontSize: 16 }} /></IconButton></Tooltip>
                        <Tooltip title="Remove"><IconButton color="error" onClick={() => onRemoveSba(i)}><DeleteIcon sx={{ fontSize: 16 }} /></IconButton></Tooltip>
                      </TableCell>
                    </TableRow>
                  ))}
                  {(draft.components || []).length > 0 && (
                    <TableRow sx={{ bgcolor: 'grey.100',
                      '& .MuiTableCell-root': { fontWeight: 800, fontSize: 12.5, py: 0.7 } }}>
                      <TableCell colSpan={4} align="right">Total</TableCell>
                      <TableCell align="right" sx={{ color: draftSbaTotal === 100 ? 'success.main' : 'warning.main' }}>
                        {draftSbaTotal}%
                      </TableCell>
                      <TableCell colSpan={2} />
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </Grid>
        )}
      </Grid>
    </Card>
  );
}

// One row in the profile list. Collapsed = compact summary; expanded =
// inline ProfileEditor with Save / Cancel / Delete buttons.
function ProfileRow({ profile, isExpanded, draft, onExpand, onCollapse, onChangeDraft,
                     onSave, onDelete, onOpenSba, onRemoveSba, onSeedGhana, saving,
                     classOptions, draftSbaTotal, draftHasExam }) {
  const modeChip = profile.mode === 'sba'
    ? <Chip color="secondary" label="SBA" sx={COMPACT_CHIP} />
    : <Chip color="primary"   label="Simple" sx={COMPACT_CHIP} />;
  return (
    <Card variant="outlined" sx={{ borderRadius: 2, overflow: 'hidden' }}>
      <Box sx={{
        p: 1.4, display: 'flex', alignItems: 'center', gap: 1.5, cursor: 'pointer',
        bgcolor: isExpanded ? 'grey.50' : '#fff',
        '&:hover': { bgcolor: 'grey.50' },
      }} onClick={() => isExpanded ? onCollapse() : onExpand()}>
        <Box sx={{
          width: 32, height: 32, borderRadius: 1.5, flexShrink: 0,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          bgcolor: profile.is_default ? 'primary.50' : 'secondary.50',
          color:   profile.is_default ? 'primary.main' : 'secondary.main',
        }}>
          <WeightIcon sx={{ fontSize: 18 }} />
        </Box>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Stack direction="row" spacing={1} alignItems="center" sx={{ flexWrap: 'wrap', rowGap: 0.4 }}>
            <Typography fontWeight={800} sx={{ fontSize: 14 }}>{profile.name}</Typography>
            {modeChip}
            {profile.is_default
              ? <Chip color="primary" variant="outlined" label="Default" sx={COMPACT_CHIP} />
              : (profile.classes || []).length > 0
                ? (profile.classes || []).map(c => (
                    <Chip key={c} label={c} sx={{ ...COMPACT_CHIP, bgcolor: 'grey.100' }} />
                  ))
                : <Chip color="warning" variant="outlined" label="No classes pinned" sx={COMPACT_CHIP} />}
          </Stack>
        </Box>
        <ExpandIcon sx={{
          color: 'text.secondary',
          transform: isExpanded ? 'rotate(180deg)' : 'none',
          transition: 'transform 0.2s',
        }} />
      </Box>
      {isExpanded && draft && (
        <Box sx={{ p: 1.5, borderTop: '1px solid', borderColor: 'divider' }}>
          <ProfileEditor
            draft={draft}
            setDraft={onChangeDraft}
            classOptions={classOptions}
            draftSbaTotal={draftSbaTotal}
            draftHasExam={draftHasExam}
            onOpenSba={onOpenSba}
            onRemoveSba={onRemoveSba}
            onSeedGhana={onSeedGhana}
          />
          <Stack direction="row" spacing={1} justifyContent="flex-end" sx={{ mt: 1.5 }}>
            {!profile.is_default && (
              <Button color="error" startIcon={<DeleteIcon />} onClick={onDelete}
                sx={{ textTransform: 'none', fontWeight: 700, mr: 'auto' }}>
                Delete profile
              </Button>
            )}
            <Button onClick={onCollapse} sx={{ textTransform: 'none' }}>Cancel</Button>
            <Button variant="contained" color="secondary" startIcon={<SaveIcon />}
              onClick={onSave} disabled={saving}
              sx={{ textTransform: 'none', fontWeight: 800 }}>
              {saving ? 'Saving…' : 'Save changes'}
            </Button>
          </Stack>
        </Box>
      )}
    </Card>
  );
}

export default function ExaminationSettingsPage() {
  const [loading, setL] = useState(true);

  // ── Period (acc) ────────────────────────────────────────────
  const [period, setPeriod] = useState({ term: '', acc: '', s_dat: '', end_dat: '', vac_dat: '' });
  const [periods, setPeriods] = useState([]);
  const [savingPeriod, setSP] = useState(false);

  // ── Assessment profiles ─────────────────────────────────────
  // Each profile holds: mode (simple/sba), the four legacy weight
  // fields, components[] (for SBA), and classes[] it applies to.
  // One profile is is_default=1 — that's the fallback when a class
  // hasn't been explicitly assigned.
  const [profiles, setProfiles]   = useState([]);
  const [classList, setClassList] = useState([]);   // [{class: 'KG1'}, …]
  const [expandedProfile, setExpandedProfile] = useState(null);
  const [editingProfile, setEditingProfile] = useState(null);   // local draft
  const [savingProfile, setSavingProfile]   = useState(false);

  // SBA component dialog (per-profile)
  const [sbaOpen, setSbaOpen] = useState(false);
  const [sbaEditing, setSbaE] = useState(null);   // { index, row }
  const [sbaForm, setSbaF]    = useState(blankSba);
  const [sbaError, setSbaErr] = useState('');

  // ── Grade bands (grade) ─────────────────────────────────────
  const [grades, setGrades] = useState([]);
  const [bandOpen, setBO]   = useState(false);
  const [bandEditing, setBE] = useState(null);
  const [bandForm, setBF]   = useState(blankBand);
  const [bandSaving, setBS] = useState(false);
  const [bandError, setBER] = useState('');

  // ── Mock / Mid-Term settings (exam_type_settings) ───────────
  //
  // A terminal exam is assembled from continuous assessment weighed
  // against an end-of-term paper — that is what Assessment Profiles
  // above describe. A mock is not assembled: the learner sits one paper
  // and gets one mark. These rows say what that paper is marked out of,
  // what it reports out of, and which grading ladder it uses.
  const [examTypes, setExamTypes]   = useState([]);
  const [etSaving, setEtSaving]     = useState('');   // exam_state in flight
  // Band editor for a type that grades on its own ladder.
  const [etBandOpen, setEtBandOpen] = useState(false);
  const [etBandState, setEtBandState] = useState(''); // which exam type
  const [etBandRows, setEtBandRows] = useState([]);
  const [etBandErr, setEtBandErr]   = useState('');

  // ── Remarks catalogue (remarks_all) ─────────────────────────
  // Options teachers pick from when filling the per-student report
  // card sheet — one list per Conduct / Attitude / Class-Teacher /
  // Headmaster bucket. Same endpoints the removed Import Remarks
  // page used, so existing data flows through untouched.
  const [remarks, setRemarks]         = useState([]);
  const [remarkOpen, setRemarkOpen]   = useState(false);
  const [remarkEditing, setRemarkE]   = useState(null);
  const [remarkForm, setRemarkF]      = useState(blankRemark);
  const [remarkSaving, setRemarkS]    = useState(false);
  const [remarkError, setRemarkErr]   = useState('');

  // ── Merit / aggregate grading settings (per acc_yer + scope) ──
  // Two levels: one "Default" row that covers every class for the year,
  // and optional per-class overrides that take precedence. The picker
  // at the top switches which scope the form is editing.
  //   scope = ''            → editing the default row
  //   scope = 'JHS 1' etc.  → editing an override
  const blankMerit = {
    show_position: false, show_grading: false,
    core_count: 0, elective_count: 0, compulsory_subjects: [],
  };
  const [meritScope, setMeritScope]         = useState('');   // '' = default
  const [meritDefault, setMeritDefault]     = useState(blankMerit);
  const [meritOverrides, setMeritOverrides] = useState([]);   // [{cla, ...}]
  const [merit, setMerit]                   = useState(blankMerit);
  const [meritSubjects, setMeritSubjects]   = useState([]);
  const [meritLoading, setMeritL]           = useState(false);
  const [meritSaving, setMeritS]            = useState(false);

  // ── Teacher portal preview (readonly admin view) ─────────────
  // Lazily loaded when the preview accordion is opened. `teachersActivity`
  // is the array of { user_id, teacher_name, teacher_email, assignments[] }
  // returned by /teacher-assignments/activity. `activityTerm` mirrors the
  // active term the progress bars were computed against.
  const [teachersActivity, setTeachersActivity] = useState([]);
  const [activityTerm, setActivityTerm]         = useState('');
  const [activityLoading, setActivityL]         = useState(false);
  const [expandedTeacher, setExpandedTeacher]   = useState(null);   // user_id
  const navigate = useNavigate();

  // ── Accordion expansion — Period is open by default ─────────
  const [expanded, setExpanded] = useState('period');
  const togglePanel = (key) => (_, isExp) => setExpanded(isExp ? key : '');

  // ── Settings scope (year + term) ────────────────────────────
  // Every year-scoped section on this page reads its data for the
  // scope selected here — NOT for whatever is currently marked active
  // in the `acc` table. This lets an admin prepare next term's / next
  // year's settings ahead of time, or fix up a closed year without
  // having to make it active first. Defaults to the active period on
  // first load and remembers the last chosen scope in localStorage so
  // it survives a page refresh.
  const [scopeYear, setScopeYear] = useState(() => localStorage.getItem('examSettingsScopeYear') || '');
  const [scopeTerm, setScopeTerm] = useState(() => localStorage.getItem('examSettingsScopeTerm') || '');
  useEffect(() => {
    if (scopeYear) localStorage.setItem('examSettingsScopeYear', scopeYear);
    if (scopeTerm) localStorage.setItem('examSettingsScopeTerm', scopeTerm);
  }, [scopeYear, scopeTerm]);
  // Once the active period lands, if the user has NEVER picked a
  // scope, snap it to the active one so first-time visitors see their
  // current term.
  useEffect(() => {
    if (period.acc && !scopeYear) setScopeYear(period.acc);
    if (period.term && !scopeTerm) setScopeTerm(period.term);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [period.acc, period.term]);
  // Full list of years the school has ever set up — the scope picker
  // pulls from this so switching between years doesn't need free-typing.
  const scopeYears = useMemo(() => {
    const set = new Set();
    for (const p of periods) if (p.acc) set.add(p.acc);
    if (period.acc) set.add(period.acc);
    if (scopeYear) set.add(scopeYear);
    return [...set].sort().reverse();
  }, [periods, period.acc, scopeYear]);
  const isActiveScope = scopeYear === period.acc && scopeTerm === period.term;

  const load = async () => {
    setL(true);
    try {
      const [main, profs, classes, catalog, etypes] = await Promise.all([
        API.get('/examination/settings'),
        API.get('/examination/settings/assessment-profiles').catch(() => ({ data: [] })),
        API.get('/classes').catch(() => ({ data: [] })),
        API.get('/examination/remarks/catalog').catch(() => ({ data: { items: [] } })),
        // Older backend without this route: the section renders empty
        // rather than taking the whole page down with it.
        API.get('/examination/exam-type-settings').catch(() => ({ data: { types: [] } })),
      ]);
      setExamTypes(etypes.data?.types || []);
      const p = main.data?.period || {};
      setPeriod({
        term:    p.term    || '',
        acc:     p.acc     || '',
        s_dat:   (p.s_dat   || '').slice(0, 10),
        end_dat: (p.end_dat || '').slice(0, 10),
        vac_dat: (p.vac_dat || '').slice(0, 10),
      });
      setPeriods(main.data?.periods || []);
      setGrades(main.data?.grades || []);
      setProfiles(profs.data || []);
      setClassList((classes.data || [])
        .map(c => c.class || c.cla || c.name).filter(Boolean));
      setRemarks(catalog.data?.items || []);
    } catch (e) {
      swal.error(e.response?.data?.message || 'Could not load settings');
    } finally { setL(false); }
  };
  useEffect(() => { load(); }, []);

  // ── Save the active academic period ─────────────────────────
  const savePeriod = async () => {
    if (!period.term)   { swal.warning('Pick a term'); return; }
    if (!period.acc)    { swal.warning('Enter an academic year (e.g. 2025/2026)'); return; }
    setSP(true);
    try {
      await API.put('/examination/settings/period', period);
      swal.success('Academic period saved');
      await load();
    } catch (e) { swal.error(e.response?.data?.message || 'Save failed'); }
    finally { setSP(false); }
  };

  const activatePeriod = async (p) => {
    const ok = await swal.confirm({
      title: 'Switch active period?',
      text: `${(p.term || '').replace('_', ' ')} - ${p.acc} will become the current period. The rest go into the closed list.`,
      tone: 'warning',
    });
    if (!ok) return;
    try {
      await API.put(`/examination/settings/period/${p.trans_code}/activate`);
      swal.success('Period activated');
      await load();
    } catch (e) { swal.error(e.response?.data?.message || 'Activate failed'); }
  };

  // ── Assessment profile helpers ──────────────────────────────
  // The page keeps a local-draft copy of one profile at a time
  // (`editingProfile`). All edits are made against the draft; saving
  // POSTs/PUTs the whole draft (components + classes included).

  const startProfile = (profile = null) => {
    // Drafting a brand-new profile or cloning the picked one for edits.
    if (profile) {
      setEditingProfile(JSON.parse(JSON.stringify(profile)));
      setExpandedProfile(profile.id);
    } else {
      setEditingProfile({
        id: null, name: '', mode: 'simple',
        cl_sc: 50, exa_sc: 50, cl_res: 60, ex_res: 100,
        is_default: 0, components: [], classes: [],
      });
      setExpandedProfile('new');
    }
  };

  const cancelEdit = () => { setEditingProfile(null); setExpandedProfile(null); };

  const saveProfile = async () => {
    const p = editingProfile;
    if (!p) return;
    if (!p.name?.trim()) { swal.warning('Profile name required'); return; }
    if (p.mode === 'simple') {
      const w = Number(p.cl_sc || 0) + Number(p.exa_sc || 0);
      if (w !== 100) {
        const ok = await swal.confirm({
          title: 'Weights do not sum to 100',
          text: `Class (${p.cl_sc}) + Exam (${p.exa_sc}) = ${w}. Save anyway?`,
          tone: 'warning',
        });
        if (!ok) return;
      }
    }
    setSavingProfile(true);
    try {
      if (p.id) await API.put(`/examination/settings/assessment-profiles/${p.id}`, p);
      else      await API.post('/examination/settings/assessment-profiles', p);
      swal.success(p.id ? 'Profile updated' : 'Profile created');
      cancelEdit();
      await load();
    } catch (e) { swal.error(e.response?.data?.message || 'Save failed'); }
    finally { setSavingProfile(false); }
  };

  const removeProfile = async (p) => {
    if (p.is_default) { swal.warning('The Default profile cannot be deleted'); return; }
    const ok = await swal.confirm({
      title: 'Delete this profile?',
      text: `Remove "${p.name}" and its assignments. Any classes pointing at it fall back to the Default profile.`,
      tone: 'danger',
    });
    if (!ok) return;
    try {
      await API.delete(`/examination/settings/assessment-profiles/${p.id}`);
      cancelEdit();
      await load();
    } catch (e) { swal.error(e.response?.data?.message || 'Delete failed'); }
  };

  // ── SBA component dialog (operates on editingProfile.components) ──
  const openSba = (idx = null) => {
    if (idx === null) {
      setSbaE(null);
      setSbaF({ ...blankSba,
        sort_order: (editingProfile?.components?.[editingProfile.components.length - 1]?.sort_order || 0) + 1 });
    } else {
      const row = editingProfile.components[idx];
      setSbaE({ index: idx });
      setSbaF({
        label: row.label, item_count: row.item_count, weight_pct: row.weight_pct,
        max_mark_per_item: row.max_mark_per_item ?? 100,
        is_exam: !!row.is_exam, sort_order: row.sort_order,
      });
    }
    setSbaErr(''); setSbaOpen(true);
  };
  const commitSba = () => {
    if (!sbaForm.label?.trim()) { setSbaErr('Label required'); return; }
    setEditingProfile(p => {
      const components = [...(p.components || [])];
      const next = {
        label: sbaForm.label.trim(),
        item_count: Math.max(1, Number(sbaForm.item_count) || 1),
        weight_pct: Math.max(0, Number(sbaForm.weight_pct) || 0),
        max_mark_per_item: Math.max(0.01, Number(sbaForm.max_mark_per_item) || 100),
        is_exam: !!sbaForm.is_exam,
        sort_order: Number(sbaForm.sort_order) || 0,
      };
      // Only one is_exam row allowed — flip any other off if this one is.
      if (next.is_exam) {
        for (let i = 0; i < components.length; i++) {
          if (sbaEditing?.index !== i) components[i] = { ...components[i], is_exam: false };
        }
      }
      if (sbaEditing) components[sbaEditing.index] = { ...components[sbaEditing.index], ...next };
      else components.push(next);
      return { ...p, components };
    });
    setSbaOpen(false);
  };
  const removeSbaRow = (idx) => {
    setEditingProfile(p => ({ ...p, components: p.components.filter((_, i) => i !== idx) }));
  };
  const seedGhanaDefaults = () => {
    setEditingProfile(p => ({
      ...p,
      mode: 'sba',
      components: [
        // Per-item maxes mirror typical Ghanaian SBA practice: short
        // continuous tasks marked out of 10, quizzes out of 20, the
        // end-of-term paper out of 100. Teachers can still adjust each
        // per profile from the editor.
        { label: 'Assignment',     item_count: 3, weight_pct: 10, max_mark_per_item: 10,  is_exam: 0, sort_order: 1 },
        { label: 'Project',        item_count: 1, weight_pct: 10, max_mark_per_item: 20,  is_exam: 0, sort_order: 2 },
        { label: 'Class Exercise', item_count: 3, weight_pct: 10, max_mark_per_item: 10,  is_exam: 0, sort_order: 3 },
        { label: 'Quiz / Test',    item_count: 2, weight_pct: 20, max_mark_per_item: 20,  is_exam: 0, sort_order: 4 },
        { label: 'End of Term Exam', item_count: 1, weight_pct: 50, max_mark_per_item: 100, is_exam: 1, sort_order: 5 },
      ],
    }));
  };

  // Aggregate readouts for the currently-edited profile.
  const draftSbaTotal = (editingProfile?.components || [])
    .reduce((s, r) => s + Number(r.weight_pct || 0), 0);
  const draftHasExam  = (editingProfile?.components || []).some(r => r.is_exam);

  // Class options the inline picker shows for class-scoped profiles —
  // hide classes already owned by ANOTHER profile so the operator
  // doesn't accidentally hijack assignments via the picker. (The save
  // endpoint also enforces this, but a clean picker is friendlier.)
  const classOptionsFor = (draft) => {
    const ownedElsewhere = new Set();
    for (const p of profiles) {
      if (p.id === draft?.id) continue;
      for (const c of p.classes || []) ownedElsewhere.add(c);
    }
    return classList.filter(c => !ownedElsewhere.has(c));
  };

  // ── Grade-band CRUD ──────────────────────────────────────────
  const openBand = (b = null) => {
    setBE(b);
    setBF(b ? { marks_1: b.marks_1, marks_2: b.marks_2, grade: b.grade, remarks: b.remarks || '' } : blankBand);
    setBER(''); setBO(true);
  };
  const saveBand = async () => {
    setBS(true); setBER('');
    try {
      if (bandEditing) await API.put(`/examination/settings/grade/${bandEditing.id}`, bandForm);
      else             await API.post('/examination/settings/grade', bandForm);
      setBO(false); await load();
    } catch (e) { setBER(e.response?.data?.message || 'Save failed'); }
    finally { setBS(false); }
  };
  const removeBand = async (b) => {
    const ok = await swal.confirm({
      title: 'Remove grade band',
      text: `Remove band ${b.marks_1}-${b.marks_2} (${b.grade})?`,
      tone: 'danger',
    });
    if (!ok) return;
    try { await API.delete(`/examination/settings/grade/${b.id}`); await load(); }
    catch (e) { swal.error(e.response?.data?.message || 'Remove failed'); }
  };

  // ── Remarks catalogue CRUD ───────────────────────────────────
  const openRemark = (cat, item = null) => {
    setRemarkE(item);
    setRemarkF(item ? { cat: item.cat, name: item.name } : { cat, name: '' });
    setRemarkErr(''); setRemarkOpen(true);
  };
  const saveRemark = async () => {
    if (!remarkForm.name?.trim()) { setRemarkErr('Remark text required'); return; }
    setRemarkS(true); setRemarkErr('');
    try {
      if (remarkEditing) await API.put(`/examination/remarks/catalog/${remarkEditing.id}`, remarkForm);
      else               await API.post('/examination/remarks/catalog', remarkForm);
      setRemarkOpen(false); await load();
    } catch (e) { setRemarkErr(e.response?.data?.message || 'Save failed'); }
    finally { setRemarkS(false); }
  };
  const removeRemark = async (it) => {
    const ok = await swal.confirm({
      title: 'Remove remark', text: `Remove "${it.name}" from ${it.cat}?`, tone: 'danger',
    });
    if (!ok) return;
    try { await API.delete(`/examination/remarks/catalog/${it.id}`); await load(); }
    catch (e) { swal.error(swal.pickError(e, 'Remove failed')); }
  };

  // ── Merit / aggregate settings ───────────────────────────────
  // The merit accordion is per-acc_yer + optional per-class scope. On
  // load we fetch:
  //   • the default row + every per-class override for the active year
  //   • the distinct subjects registered for the year (drives the
  //     compulsory-subjects picker)
  const loadMerit = async () => {
    const yr = (scopeYear || period.acc || '').trim();
    if (!yr) return;
    setMeritL(true);
    try {
      const [ms, subs] = await Promise.all([
        API.get('/examination/settings/merit', { params: { acc_yer: yr } }),
        API.get('/examination/subjects', { params: { acc_yer: yr } }).catch(() => ({ data: { rows: [] } })),
      ]);
      const def = ms.data?.default || blankMerit;
      const ovs = Array.isArray(ms.data?.overrides) ? ms.data.overrides : [];
      setMeritDefault(def);
      setMeritOverrides(ovs);
      // Reload whichever scope was being edited; fall back to default
      // if that override was deleted server-side.
      const active = meritScope
        ? (ovs.find(o => o.cla === meritScope) || null)
        : def;
      if (!active) { setMeritScope(''); setMerit(def); }
      else         { setMerit({
        show_position:       !!active.show_position,
        show_grading:        !!active.show_grading,
        core_count:          Number(active.core_count || 0),
        elective_count:      Number(active.elective_count || 0),
        compulsory_subjects: Array.isArray(active.compulsory_subjects) ? active.compulsory_subjects : [],
      }); }
      const list = subs.data?.rows || subs.data || [];
      const uniq = [...new Set(list.map(r => r.sub).filter(Boolean))].sort((a, b) => a.localeCompare(b));
      setMeritSubjects(uniq);
    } catch (e) { swal.error(e.response?.data?.message || 'Could not load merit settings'); }
    finally { setMeritL(false); }
  };

  // Auto-load whenever the merit accordion is opened OR the scope year
  // changes. Skips silently if no year is picked.
  useEffect(() => {
    if (expanded === 'merit' && (scopeYear || period.acc)) loadMerit();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [expanded, scopeYear, period.acc]);

  // Switch to editing a different scope (default or a specific class).
  // If the override doesn't exist yet, seed the form with the default's
  // values so the operator has a sensible starting point.
  const switchMeritScope = (cla) => {
    setMeritScope(cla);
    if (!cla) { setMerit(meritDefault); return; }
    const existing = meritOverrides.find(o => o.cla === cla);
    setMerit(existing ? {
      show_position:       !!existing.show_position,
      show_grading:        !!existing.show_grading,
      core_count:          Number(existing.core_count || 0),
      elective_count:      Number(existing.elective_count || 0),
      compulsory_subjects: Array.isArray(existing.compulsory_subjects) ? existing.compulsory_subjects : [],
    } : { ...meritDefault });
  };

  const saveMerit = async () => {
    const yr = (scopeYear || period.acc || '').trim();
    if (!yr) { swal.warning('Set the active academic period first'); return; }
    setMeritS(true);
    try {
      await API.put('/examination/settings/merit',
        { ...merit, acc_yer: yr, cla: meritScope });
      swal.success(meritScope ? `Settings saved for ${meritScope}` : 'Default settings saved');
      await loadMerit();
    } catch (e) { swal.error(e.response?.data?.message || 'Save failed'); }
    finally { setMeritS(false); }
  };

  const removeMeritOverride = async (cla) => {
    const yr = (scopeYear || period.acc || '').trim();
    if (!yr || !cla) return;
    const ok = await swal.confirm({
      title: 'Remove override?',
      text: `${cla} will fall back to the default merit settings for ${yr}.`,
      tone: 'warning',
    });
    if (!ok) return;
    try {
      await API.delete('/examination/settings/merit',
        { params: { acc_yer: yr, cla } });
      if (meritScope === cla) setMeritScope('');
      await loadMerit();
    } catch (e) { swal.error(e.response?.data?.message || 'Remove failed'); }
  };

  // ── Teacher portal preview ───────────────────────────────────
  const loadTeacherActivity = async () => {
    const yr = (scopeYear || period.acc || '').trim();
    if (!yr) return;
    setActivityL(true);
    try {
      const r = await API.get('/teacher-assignments/activity', { params: { acc_yer: yr } });
      setTeachersActivity(Array.isArray(r.data?.teachers) ? r.data.teachers : []);
      setActivityTerm(r.data?.term || '');
    } catch (e) { swal.error(e.response?.data?.message || 'Could not load teacher activity'); }
    finally { setActivityL(false); }
  };
  useEffect(() => {
    if (expanded === 'preview' && (scopeYear || period.acc)) loadTeacherActivity();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [expanded, scopeYear, period.acc]);

  // ── Preview blurbs shown in collapsed accordion headers ─────
  const periodPreview = period.term && period.acc
    ? <Chip size="small" color="primary" sx={{ fontWeight: 700, height: 22 }} label={`${(period.term || '').replace('_', ' ')} - ${period.acc}`} />
    : <Chip size="small" color="warning" sx={{ fontWeight: 700, height: 22 }} label="Not set" />;
  const weightPreview = profiles.length === 0
    ? <Chip size="small" color="warning" sx={{ fontWeight: 700, height: 22 }} label="Not set" />
    : <Chip size="small" color="primary" sx={{ fontWeight: 700, height: 22 }}
        label={`${profiles.length} profile(s)`} />;
  const gradePreview = grades.length > 0
    ? <Chip size="small" color="primary" sx={{ fontWeight: 700, height: 22 }} label={`${grades.length} band(s)`} />
    : <Chip size="small" color="warning" sx={{ fontWeight: 700, height: 22 }} label="No bands" />;
  const remarksPreview = remarks.length > 0
    ? <Chip size="small" color="primary" sx={{ fontWeight: 700, height: 22 }} label={`${remarks.length} option(s)`} />
    : <Chip size="small" color="warning" sx={{ fontWeight: 700, height: 22 }} label="Empty" />;
  const examTypePreview = examTypes.length === 0
    ? <Chip size="small" color="warning" sx={{ fontWeight: 700, height: 22 }} label="Not set" />
    : <Chip size="small" color="secondary" sx={{ fontWeight: 700, height: 22 }}
        label={examTypes.map(t => `${t.exam_state} /${Number(t.max_score)}`).join(' · ')} />;

  // ── Mock / Mid-Term handlers ────────────────────────────────
  // Edits are held locally and written on Save, because changing what a
  // paper is marked out of changes how every mark on it grades — not
  // something to fire off on each keystroke.
  const patchExamType = (state, patch) => {
    setExamTypes(list => list.map(t =>
      t.exam_state === state ? { ...t, ...patch } : t));
  };

  const saveExamType = async (t) => {
    const max   = Number(t.max_score);
    const scale = Number(t.scale_to);
    if (!Number.isFinite(max) || max <= 0) {
      return swal.error('"Marked out of" must be greater than 0', 'Check the figure');
    }
    if (!Number.isFinite(scale) || scale <= 0) {
      return swal.error('"Reported out of" must be greater than 0', 'Check the figure');
    }
    setEtSaving(t.exam_state);
    try {
      const r = await API.put(
        `/examination/exam-type-settings/${encodeURIComponent(t.exam_state)}`,
        {
          max_score: max,
          scale_to:  scale,
          use_global_grades: t.use_global_grades ? 1 : 0,
        });
      setExamTypes(list => list.map(x =>
        x.exam_state === t.exam_state ? { ...x, ...r.data } : x));
      swal.success(`${t.exam_state} settings saved`);
    } catch (e) {
      swal.error(swal.pickError(e, 'Could not save'));
    } finally { setEtSaving(''); }
  };

  // Open the per-type band editor, seeded from whatever that type has —
  // or from the school scale, so "its own ladder" starts as a copy to
  // adjust rather than an empty table.
  const openExamBands = (t) => {
    const seed = (t.bands && t.bands.length ? t.bands : grades).map(b => ({
      marks_1: b.marks_1, marks_2: b.marks_2,
      grade: b.grade || '', remarks: b.remarks || '',
    }));
    setEtBandState(t.exam_state);
    setEtBandRows(seed.length ? seed : [{ marks_1: 0, marks_2: 100, grade: '', remarks: '' }]);
    setEtBandErr('');
    setEtBandOpen(true);
  };

  const saveExamBands = async () => {
    const rows = etBandRows
      .map(b => ({
        marks_1: Number(b.marks_1), marks_2: Number(b.marks_2),
        grade: String(b.grade || '').trim(), remarks: String(b.remarks || '').trim(),
      }))
      .filter(b => b.grade !== '');
    if (!rows.length) { setEtBandErr('Add at least one band, or switch back to the school scale.'); return; }
    for (const b of rows) {
      if (!Number.isFinite(b.marks_1) || !Number.isFinite(b.marks_2)) {
        setEtBandErr(`Band "${b.grade}" needs both a From and a To mark.`); return;
      }
      if (b.marks_1 > b.marks_2) {
        setEtBandErr(`Band "${b.grade}": From (${b.marks_1}) is above To (${b.marks_2}).`); return;
      }
    }
    const t = examTypes.find(x => x.exam_state === etBandState);
    setEtSaving(etBandState);
    try {
      const r = await API.put(
        `/examination/exam-type-settings/${encodeURIComponent(etBandState)}`,
        {
          max_score: Number(t?.max_score) || 100,
          scale_to:  Number(t?.scale_to)  || 100,
          use_global_grades: 0,
          bands: rows,
        });
      setExamTypes(list => list.map(x =>
        x.exam_state === etBandState ? { ...x, ...r.data } : x));
      setEtBandOpen(false);
      swal.success(`${etBandState} grading scale saved`);
    } catch (e) {
      setEtBandErr(swal.pickError(e, 'Could not save the bands'));
    } finally { setEtSaving(''); }
  };
  // Preview chip for the Teacher Portal Preview accordion — shows how
  // many teachers are covered + an aggregate progress across every
  // (state × subject) card so the admin sees at a glance whether the
  // term is running behind on score entry.
  const activityTotals = teachersActivity.reduce((acc, t) => {
    for (const a of t.assignments || []) {
      for (const st of a.states || []) {
        acc.entered += st.entered || 0;
        acc.total   += st.total   || 0;
      }
    }
    return acc;
  }, { entered: 0, total: 0 });
  const activityPct = activityTotals.total > 0
    ? Math.round((activityTotals.entered / activityTotals.total) * 100)
    : 0;
  const previewPreview = teachersActivity.length > 0
    ? <Stack direction="row" spacing={0.6} alignItems="center">
        <Chip size="small" color="primary" sx={{ fontWeight: 700, height: 22 }}
          label={`${teachersActivity.length} teacher${teachersActivity.length === 1 ? '' : 's'}`} />
        <Chip size="small" color={activityPct >= 75 ? 'success' : activityPct >= 40 ? 'warning' : 'default'}
          variant={activityPct === 0 ? 'outlined' : 'filled'}
          sx={{ fontWeight: 700, height: 22 }}
          label={`${activityPct}% entered`} />
      </Stack>
    : <Chip size="small" variant="outlined" sx={{ fontWeight: 700, height: 22 }} label="Click to load" />;

  const meritDefLabel = [
    meritDefault.show_position ? 'Position' : null,
    meritDefault.show_grading  ? 'Grading'  : null,
  ].filter(Boolean).join(' + ') || 'Both off';
  const meritPreview = (
    <Stack direction="row" spacing={0.6} alignItems="center">
      <Chip size="small"
        color={(meritDefault.show_position || meritDefault.show_grading) ? 'primary' : 'default'}
        variant={(meritDefault.show_position || meritDefault.show_grading) ? 'filled' : 'outlined'}
        sx={{ fontWeight: 700, height: 22 }}
        label={`Default: ${meritDefLabel}`} />
      {meritOverrides.length > 0 && (
        <Chip size="small" color="warning" sx={{ fontWeight: 700, height: 22 }}
          label={`+${meritOverrides.length} class override${meritOverrides.length === 1 ? '' : 's'}`} />
      )}
    </Stack>
  );

  return (
    <Box sx={{ maxWidth: 1600, mx: 'auto' }}>
      <ModuleHeader
        icon={<SettingsIcon />}
        title="Examination Settings"
        subtitle="Academic period, grading scale, and the class/exam weights used to compute each subject's overall mark"
      />

      {loading && (
        <Card sx={{ p: 6, textAlign: 'center' }}>
          <CircularProgress />
        </Card>
      )}

      {!loading && (
        <Stack spacing={1.5}>

          {/* ─────────────── Settings Scope banner ─────────────── */}
          {/* Every year-scoped section on this page reads its data
              for the year+term selected here. Global sections (Grading
              Scale + Remarks Catalogue) ignore the scope — they apply
              across every year, marked with the "Global" chip on their
              accordion. */}
          <Card variant="outlined" sx={{
            p: 1.5, borderRadius: 2,
            bgcolor: isActiveScope ? 'primary.50' : 'warning.50',
            borderColor: isActiveScope ? 'primary.main' : 'warning.main',
            borderWidth: 2,
          }}>
            <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.5}
              alignItems={{ md: 'center' }}>
              <Stack direction="row" spacing={1} alignItems="center" sx={{ flex: 1, minWidth: 0 }}>
                <PeriodIcon sx={{ color: isActiveScope ? 'primary.dark' : 'warning.dark' }} />
                <Box sx={{ minWidth: 0 }}>
                  <Typography sx={{ fontWeight: 800, fontSize: 13, lineHeight: 1.2,
                    color: isActiveScope ? 'primary.dark' : 'warning.dark' }}>
                    Editing settings for {scopeYear || '—'} · {(scopeTerm || '').replace('_', ' ') || '—'}
                  </Typography>
                  <Typography variant="caption" sx={{ opacity: 0.85, display: 'block', lineHeight: 1.2 }}>
                    {isActiveScope
                      ? 'This is the ACTIVE period — changes affect what teachers see right now.'
                      : 'You are editing a NON-active period. Teachers are still on the active period below.'}
                  </Typography>
                </Box>
              </Stack>
              <TextField select size="small" label="Year"
                value={scopeYear}
                onChange={e => setScopeYear(e.target.value)}
                sx={{ minWidth: 160, bgcolor: '#fff' }}>
                {scopeYears.length === 0 && (
                  <MenuItem value=""><i>None saved yet</i></MenuItem>
                )}
                {scopeYears.map(y => (
                  <MenuItem key={y} value={y}>
                    {y}{y === period.acc ? ' · active' : ''}
                  </MenuItem>
                ))}
              </TextField>
              <TextField select size="small" label="Term"
                value={scopeTerm}
                onChange={e => setScopeTerm(e.target.value)}
                sx={{ minWidth: 130, bgcolor: '#fff' }}>
                {['Term_1', 'Term_2', 'Term_3'].map(t => (
                  <MenuItem key={t} value={t}>
                    {t.replace('_', ' ')}{t === period.term ? ' · active' : ''}
                  </MenuItem>
                ))}
              </TextField>
              {!isActiveScope && period.acc && (
                <Button size="small" variant="outlined" color="warning"
                  onClick={() => { setScopeYear(period.acc); setScopeTerm(period.term); }}
                  sx={{ textTransform: 'none', fontWeight: 700 }}>
                  Jump to active
                </Button>
              )}
            </Stack>
          </Card>

          {/* ─────────────── 1. Academic Period ─────────────── */}
          <Accordion expanded={expanded === 'period'} onChange={togglePanel('period')}
            elevation={0} sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 2, '&:before': { display: 'none' } }}>
            <AccordionSummary expandIcon={<ExpandIcon />} sx={{ '& .MuiAccordionSummary-content': { my: 1.5 } }}>
              <SectionTitle
                icon={<PeriodIcon />}
                color="primary"
                title="Academic Period (Year & Term)"
                subtitle="The current academic year, the term we are in, and when it begins/ends"
                preview={periodPreview}
              />
            </AccordionSummary>
            <AccordionDetails>
              <Alert severity="info" variant="outlined" sx={{ mb: 2 }}>
                Stored in the legacy <code>acc</code> table. Whatever you save here becomes the current period
                (<code>state = 0</code>); any previously-current row is moved to the closed list below.
              </Alert>

              <Grid container spacing={1.5}>
                <Grid item xs={12} sm={6}>
                  <TextField select fullWidth size="small" label="Term"
                    value={period.term}
                    onChange={e => setPeriod(p => ({ ...p, term: e.target.value }))}
                    helperText="Canonical codes (Term_1 / Term_2 / Term_3) — must match the rest of the system so search and joins line up.">
                    <MenuItem value=""><em>— Pick —</em></MenuItem>
                    {['Term_1', 'Term_2', 'Term_3'].map(t => (
                      <MenuItem key={t} value={t}>{t.replace('_', ' ')}</MenuItem>
                    ))}
                  </TextField>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField fullWidth size="small" label="Academic Year"
                    placeholder="e.g. 2025/2026"
                    value={period.acc}
                    onChange={e => setPeriod(p => ({ ...p, acc: e.target.value }))}
                    helperText="Format: YYYY/YYYY" />
                </Grid>
                <Grid item xs={12} sm={4}>
                  <TextField fullWidth size="small" type="date" label="Term begins"
                    InputLabelProps={{ shrink: true }}
                    value={period.s_dat || ''}
                    onChange={e => setPeriod(p => ({ ...p, s_dat: e.target.value }))}
                    helperText="First school day of the term." />
                </Grid>
                <Grid item xs={12} sm={4}>
                  <TextField fullWidth size="small" type="date" label="Term ends"
                    InputLabelProps={{ shrink: true }}
                    value={period.end_dat || ''}
                    onChange={e => setPeriod(p => ({ ...p, end_dat: e.target.value }))}
                    helperText="Last teaching day — drives the Maximum Attendance count." />
                </Grid>
                <Grid item xs={12} sm={4}>
                  <TextField fullWidth size="small" type="date" label="Reopening date"
                    InputLabelProps={{ shrink: true }}
                    value={period.vac_dat || ''}
                    onChange={e => setPeriod(p => ({ ...p, vac_dat: e.target.value }))}
                    helperText="Day school reopens for the next term — printed on the report card." />
                </Grid>
              </Grid>

              <Box sx={{ mt: 2, display: 'flex', justifyContent: 'flex-end' }}>
                <Button variant="contained" startIcon={<SaveIcon />} onClick={savePeriod}
                  disabled={savingPeriod}
                  sx={{ textTransform: 'none', fontWeight: 800 }}>
                  {savingPeriod ? 'Saving…' : 'Save active period'}
                </Button>
              </Box>

              {/* Periods history */}
              {periods.length > 0 && (
                <Box sx={{ mt: 3 }}>
                  <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
                    <TermIcon fontSize="small" color="action" />
                    <Typography variant="caption" sx={{ fontWeight: 800, letterSpacing: 0.5, textTransform: 'uppercase', color: 'text.secondary' }}>
                      Previously set periods
                    </Typography>
                  </Stack>
                  <TableContainer sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
                    <Table size="small" sx={COMPACT_TABLE}>
                      <TableHead>
                        <TableRow>
                          <TableCell>Term</TableCell>
                          <TableCell>Year</TableCell>
                          <TableCell>Begins</TableCell>
                          <TableCell>Ends</TableCell>
                          <TableCell>Reopens</TableCell>
                          <TableCell sx={{ width: 80 }}>Status</TableCell>
                          <TableCell align="right" sx={{ width: 50 }} />
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {periods.map(p => (
                          <TableRow key={p.trans_code} hover>
                            <TableCell sx={{ fontWeight: 700 }}>{(p.term || '').replace('_', ' ')}</TableCell>
                            <TableCell>{p.acc}</TableCell>
                            <TableCell sx={{ color: p.s_dat ? 'text.primary' : 'text.disabled' }}>{(p.s_dat || '—').slice(0, 10)}</TableCell>
                            <TableCell sx={{ color: p.end_dat ? 'text.primary' : 'text.disabled' }}>{(p.end_dat || '—').slice(0, 10)}</TableCell>
                            <TableCell sx={{ color: p.vac_dat ? 'text.primary' : 'text.disabled' }}>{(p.vac_dat || '—').slice(0, 10)}</TableCell>
                            <TableCell>
                              {p.state === 0
                                ? <Chip color="success" label="Active" sx={COMPACT_CHIP} />
                                : <Chip variant="outlined" label="Closed" sx={COMPACT_CHIP} />}
                            </TableCell>
                            <TableCell align="right">
                              {p.state !== 0 && (
                                <Tooltip title="Activate this period">
                                  <IconButton color="primary" onClick={() => activatePeriod(p)}>
                                    <ActivateIcon sx={{ fontSize: 16 }} />
                                  </IconButton>
                                </Tooltip>
                              )}
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Box>
              )}
            </AccordionDetails>
          </Accordion>

          {/* ─────────────── 2. Weighting (Assessment Profiles) ─────────────── */}
          <Accordion expanded={expanded === 'weight'} onChange={togglePanel('weight')}
            elevation={0} sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 2, '&:before': { display: 'none' } }}>
            <AccordionSummary expandIcon={<ExpandIcon />} sx={{ '& .MuiAccordionSummary-content': { my: 1.5 } }}>
              <SectionTitle
                icon={<WeightIcon />}
                color="secondary"
                title="Assessment Profiles"
                subtitle="Configure one or more profiles (Simple or SBA) and pin each to specific classes"
                preview={weightPreview}
              />
            </AccordionSummary>
            <AccordionDetails>
              <Alert severity="info" variant="outlined" sx={{ mb: 2 }}>
                The <b>Default</b> profile applies to every class that isn't pinned to
                another profile. Add a new profile to override the model for a specific
                group — e.g. SBA for JHS classes while KG stays on the Simple model.
              </Alert>

              {/* Profile list — compact rows, click to expand inline */}
              <Stack spacing={1}>
                {profiles.map(p => (
                  <ProfileRow
                    key={p.id}
                    profile={p}
                    isExpanded={expandedProfile === p.id}
                    draft={editingProfile?.id === p.id ? editingProfile : null}
                    onExpand={() => startProfile(p)}
                    onCollapse={cancelEdit}
                    onChangeDraft={setEditingProfile}
                    onSave={saveProfile}
                    onDelete={() => removeProfile(p)}
                    onOpenSba={openSba}
                    onRemoveSba={removeSbaRow}
                    onSeedGhana={seedGhanaDefaults}
                    saving={savingProfile}
                    classOptions={classOptionsFor(editingProfile)}
                    draftSbaTotal={draftSbaTotal}
                    draftHasExam={draftHasExam}
                  />
                ))}
              </Stack>

              {/* New profile drawer */}
              {expandedProfile === 'new' && editingProfile && !editingProfile.id ? (
                <Box sx={{ mt: 1.5 }}>
                  <ProfileEditor
                    draft={editingProfile}
                    setDraft={setEditingProfile}
                    classOptions={classOptionsFor(editingProfile)}
                    draftSbaTotal={draftSbaTotal}
                    draftHasExam={draftHasExam}
                    onOpenSba={openSba}
                    onRemoveSba={removeSbaRow}
                    onSeedGhana={seedGhanaDefaults}
                  />
                  <Stack direction="row" spacing={1} justifyContent="flex-end" sx={{ mt: 1.5 }}>
                    <Button onClick={cancelEdit} sx={{ textTransform: 'none' }}>Cancel</Button>
                    <Button variant="contained" color="secondary" startIcon={<SaveIcon />}
                      onClick={saveProfile} disabled={savingProfile}
                      sx={{ textTransform: 'none', fontWeight: 800 }}>
                      {savingProfile ? 'Saving…' : 'Create profile'}
                    </Button>
                  </Stack>
                </Box>
              ) : (
                <Box sx={{ mt: 1.5, display: 'flex', justifyContent: 'flex-end' }}>
                  <Button variant="outlined" color="secondary" startIcon={<AddIcon />}
                    onClick={() => startProfile(null)}
                    sx={{ textTransform: 'none', fontWeight: 800 }}>
                    Add new profile
                  </Button>
                </Box>
              )}
            </AccordionDetails>
          </Accordion>

          {/* ────── 2b. Mock & Mid-Term (single-score exams) ────── */}
          <Accordion expanded={expanded === 'examtypes'} onChange={togglePanel('examtypes')}
            elevation={0} sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 2, '&:before': { display: 'none' } }}>
            <AccordionSummary expandIcon={<ExpandIcon />} sx={{ '& .MuiAccordionSummary-content': { my: 1.5 } }}>
              <SectionTitle
                icon={<WeightIcon />}
                color="secondary"
                title="Mock & Mid-Term Exams"
                subtitle="One paper, one mark — what it is marked out of and how it grades"
                preview={examTypePreview}
              />
            </AccordionSummary>
            <AccordionDetails>
              <Alert severity="info" variant="outlined" sx={{ mb: 2 }}>
                These do <b>not</b> use the Assessment Profiles above. A terminal exam is
                assembled — class work weighed against an end-of-term paper. A mock is not:
                the learner sits one paper and gets one mark, so the teacher is given a
                single <b>Score</b> box and nothing else.
              </Alert>

              {examTypes.length === 0 && (
                <Alert severity="warning" variant="outlined">
                  Nothing to configure yet. Restart the backend once — it creates these
                  rows on boot — or run <code>migrations/2026-09-17-exam-type-settings.sql</code>.
                </Alert>
              )}

              <Stack spacing={2}>
                {examTypes.map(t => {
                  const scales = Number(t.max_score) !== Number(t.scale_to);
                  return (
                    <Card key={t.exam_state} variant="outlined" sx={{ borderRadius: 2, p: 2 }}>
                      <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1.5 }}>
                        <Typography sx={{ fontWeight: 800, fontSize: 14 }}>
                          {t.exam_state}
                        </Typography>
                        <Chip size="small" label="Single score" color="secondary"
                          variant="outlined" sx={COMPACT_CHIP} />
                        <Box sx={{ flex: 1 }} />
                        <Button size="small" variant="contained" color="secondary"
                          disabled={etSaving === t.exam_state}
                          onClick={() => saveExamType(t)}
                          startIcon={etSaving === t.exam_state
                            ? <CircularProgress size={13} color="inherit" /> : <SaveIcon />}
                          sx={{ textTransform: 'none', fontWeight: 800 }}>
                          Save
                        </Button>
                      </Stack>

                      <Grid container spacing={1.5}>
                        <Grid item xs={12} sm={4}>
                          <TextField fullWidth size="small" type="number"
                            label="Marked out of"
                            value={t.max_score ?? 100}
                            onChange={e => patchExamType(t.exam_state, { max_score: e.target.value })}
                            inputProps={{ min: 1, step: '0.01' }}
                            helperText="What the paper itself is out of" />
                        </Grid>
                        <Grid item xs={12} sm={4}>
                          <TextField fullWidth size="small" type="number"
                            label="Reported out of"
                            value={t.scale_to ?? 100}
                            onChange={e => patchExamType(t.exam_state, { scale_to: e.target.value })}
                            inputProps={{ min: 1, step: '0.01' }}
                            helperText={scales
                              ? `Scaled: a mark of ${t.max_score} becomes ${t.scale_to}`
                              : 'Same as above — no scaling'} />
                        </Grid>
                        <Grid item xs={12} sm={4}>
                          <TextField select fullWidth size="small"
                            label="Grading scale"
                            value={t.use_global_grades ? 'global' : 'own'}
                            onChange={e => {
                              const own = e.target.value === 'own';
                              patchExamType(t.exam_state, { use_global_grades: own ? 0 : 1 });
                              if (own) openExamBands({ ...t, use_global_grades: 0 });
                            }}
                            helperText={t.use_global_grades
                              ? `The school scale (${grades.length} band${grades.length === 1 ? '' : 's'})`
                              : `Its own (${(t.bands || []).length} band${(t.bands || []).length === 1 ? '' : 's'})`}>
                            <MenuItem value="global">Use the school grading scale</MenuItem>
                            <MenuItem value="own">Use its own bands</MenuItem>
                          </TextField>
                        </Grid>
                      </Grid>

                      {!t.use_global_grades && (
                        <Stack direction="row" alignItems="center" spacing={1} sx={{ mt: 1.5 }}>
                          <Button size="small" variant="outlined" startIcon={<EditIcon />}
                            onClick={() => openExamBands(t)}
                            sx={{ textTransform: 'none', fontWeight: 800 }}>
                            Edit {t.exam_state} bands
                          </Button>
                          <Stack direction="row" spacing={0.5} sx={{ flexWrap: 'wrap', gap: 0.5 }}>
                            {(t.bands || []).map((b, i) => (
                              <Chip key={i} size="small" variant="outlined"
                                label={`${b.marks_1}-${b.marks_2} ${b.grade}`}
                                sx={COMPACT_CHIP} />
                            ))}
                            {(t.bands || []).length === 0 && (
                              <Typography variant="caption" color="warning.main" sx={{ fontWeight: 700 }}>
                                No bands set yet — the school scale is still being used.
                              </Typography>
                            )}
                          </Stack>
                        </Stack>
                      )}
                    </Card>
                  );
                })}
              </Stack>
            </AccordionDetails>
          </Accordion>

          {/* ─────────────── 3. Grading Scale ─────────────── */}
          <Accordion expanded={expanded === 'grade'} onChange={togglePanel('grade')}
            elevation={0} sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 2, '&:before': { display: 'none' } }}>
            <AccordionSummary expandIcon={<ExpandIcon />} sx={{ '& .MuiAccordionSummary-content': { my: 1.5 } }}>
              <SectionTitle
                icon={<GradeIcon />}
                color="success"
                title={<>Subject Grading Scale <Chip label="Global" size="small" color="default" variant="outlined" sx={{ ml: 0.8, height: 18, fontSize: 9.5, fontWeight: 800 }} /></>}
                subtitle="Score ranges that map to a letter grade and remark — same for every academic year"
                preview={gradePreview}
              />
            </AccordionSummary>
            <AccordionDetails>
              <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1.4 }}>
                <Alert severity="info" variant="outlined" sx={{ flex: 1, mr: 1.5 }}>
                  Ranges must <b>not overlap</b> — overlapping bands would make the lookup ambiguous.
                </Alert>
                <Button variant="contained" color="success" size="small" startIcon={<AddIcon />}
                  onClick={() => openBand(null)}
                  sx={{ textTransform: 'none', fontWeight: 800 }}>
                  Add band
                </Button>
              </Stack>

              <TableContainer sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
                <Table size="small" sx={COMPACT_TABLE}>
                  <TableHead>
                    <TableRow>
                      <TableCell align="right" sx={{ width: 70 }}>From</TableCell>
                      <TableCell align="right" sx={{ width: 70 }}>To</TableCell>
                      <TableCell sx={{ width: 80 }}>Grade</TableCell>
                      <TableCell>Remarks</TableCell>
                      <TableCell align="right" sx={{ width: 70 }} />
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {grades.length === 0 && (
                      <TableRow>
                        <TableCell colSpan={5} align="center"
                          sx={{ py: 2.5, color: 'text.secondary', fontStyle: 'italic' }}>
                          No grade bands yet — click <b>Add band</b>.
                        </TableCell>
                      </TableRow>
                    )}
                    {grades.map(g => (
                      <TableRow key={g.id} hover>
                        <TableCell align="right" sx={{ fontWeight: 700 }}>{g.marks_1}</TableCell>
                        <TableCell align="right" sx={{ fontWeight: 700 }}>{g.marks_2}</TableCell>
                        <TableCell><Chip label={g.grade} color="primary" sx={COMPACT_CHIP} /></TableCell>
                        <TableCell sx={{ color: g.remarks ? 'text.primary' : 'text.disabled' }}>
                          {g.remarks || '—'}
                        </TableCell>
                        <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
                          <Tooltip title="Edit"><IconButton onClick={() => openBand(g)}><EditIcon sx={{ fontSize: 16 }} /></IconButton></Tooltip>
                          <Tooltip title="Remove"><IconButton color="error" onClick={() => removeBand(g)}><DeleteIcon sx={{ fontSize: 16 }} /></IconButton></Tooltip>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </AccordionDetails>
          </Accordion>

          {/* ─────────────── 4. Remarks Catalogue ─────────────── */}
          <Accordion expanded={expanded === 'remarks'} onChange={togglePanel('remarks')}
            elevation={0} sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 2, '&:before': { display: 'none' } }}>
            <AccordionSummary expandIcon={<ExpandIcon />} sx={{ '& .MuiAccordionSummary-content': { my: 1.5 } }}>
              <SectionTitle
                icon={<RemarksIcon />}
                color="info"
                title={<>Remarks Catalogue <Chip label="Global" size="small" color="default" variant="outlined" sx={{ ml: 0.8, height: 18, fontSize: 9.5, fontWeight: 800 }} /></>}
                subtitle="Conduct, Attitude and Remark options — same catalogue for every academic year"
                preview={remarksPreview}
              />
            </AccordionSummary>
            <AccordionDetails>
              <Alert severity="info" variant="outlined" sx={{ mb: 1.5 }}>
                Options saved here appear as pick-lists on the teacher portal when
                filling report cards. Add as many phrases as you need per bucket.
              </Alert>

              <Grid container spacing={1.5}>
                {REMARK_CATEGORIES.map(({ cat, label }) => {
                  const list = remarks.filter(r => r.cat === cat);
                  return (
                    <Grid item xs={12} md={6} key={cat}>
                      <Box sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1.5, overflow: 'hidden', height: '100%' }}>
                        <Stack direction="row" alignItems="center" spacing={1}
                          sx={{ px: 1.5, py: 0.9, bgcolor: 'grey.50', borderBottom: '1px solid', borderColor: 'divider' }}>
                          <Typography sx={{ fontWeight: 800, fontSize: 13, flex: 1 }}>{label}</Typography>
                          <Chip size="small" label={list.length} color="primary" sx={COMPACT_CHIP} />
                          <Button size="small" startIcon={<AddIcon />} onClick={() => openRemark(cat)}
                            sx={{ textTransform: 'none', fontWeight: 700 }}>
                            Add
                          </Button>
                        </Stack>
                        <TableContainer sx={{ maxHeight: 300, overflowY: 'auto' }}>
                          <Table size="small" stickyHeader sx={COMPACT_TABLE}>
                            <TableBody>
                              {list.length === 0 && (
                                <TableRow>
                                  <TableCell colSpan={2} align="center"
                                    sx={{ py: 1.5, color: 'text.secondary', fontStyle: 'italic', fontSize: 12 }}>
                                    Empty — click <b>Add</b>.
                                  </TableCell>
                                </TableRow>
                              )}
                              {list.map(it => (
                                <TableRow key={it.id} hover>
                                  <TableCell>{it.name}</TableCell>
                                  <TableCell align="right" sx={{ width: 90, whiteSpace: 'nowrap' }}>
                                    <Tooltip title="Edit"><IconButton onClick={() => openRemark(cat, it)}><EditIcon sx={{ fontSize: 16 }} /></IconButton></Tooltip>
                                    <Tooltip title="Remove"><IconButton color="error" onClick={() => removeRemark(it)}><DeleteIcon sx={{ fontSize: 16 }} /></IconButton></Tooltip>
                                  </TableCell>
                                </TableRow>
                              ))}
                            </TableBody>
                          </Table>
                        </TableContainer>
                      </Box>
                    </Grid>
                  );
                })}
              </Grid>
            </AccordionDetails>
          </Accordion>

          {/* ─────────────── 5. Merit / Examination Grading ─────────────── */}
          <Accordion expanded={expanded === 'merit'} onChange={togglePanel('merit')}
            elevation={0} sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 2, '&:before': { display: 'none' } }}>
            <AccordionSummary expandIcon={<ExpandIcon />} sx={{ '& .MuiAccordionSummary-content': { my: 1.5 } }}>
              <SectionTitle
                icon={<MeritIcon />}
                color="warning"
                title="Merit / Examination Grading Scale"
                subtitle="Choose whether report cards show class position and the Ghana-style aggregate grade — configured per academic year"
                preview={meritPreview}
              />
            </AccordionSummary>
            <AccordionDetails>
              {!scopeYear ? (
                <Alert severity="warning" variant="outlined">
                  Pick a year in the <b>Settings Scope</b> banner above first — merit settings are stored per academic year.
                </Alert>
              ) : (
                <Stack spacing={2}>
                  <Alert severity="info" variant="outlined">
                    Editing merit settings for <b>{scopeYear}</b>. Set a <b>Default</b> for every class, then optionally
                    override for specific classes. Compulsory subjects are picked from the ones already
                    registered for this year on the <b>Register Subject</b> page ({meritSubjects.length} available).
                  </Alert>

                  {/* Scope picker — default vs per-class override */}
                  <Box sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1.5, p: 1.5 }}>
                    <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.5} alignItems={{ md: 'center' }}>
                      <TextField select size="small" label="Applies to"
                        value={meritScope}
                        onChange={e => switchMeritScope(e.target.value)}
                        sx={{ minWidth: 260 }}>
                        <MenuItem value="">
                          <b>Default — all classes</b>
                        </MenuItem>
                        {classList.map(c => {
                          const isOverride = meritOverrides.some(o => o.cla === c);
                          return (
                            <MenuItem key={c} value={c}>
                              {c}{isOverride ? ' · has override' : ''}
                            </MenuItem>
                          );
                        })}
                      </TextField>
                      <Typography variant="caption" color="text.secondary" sx={{ flex: 1 }}>
                        {meritScope
                          ? <>Editing override for <b>{meritScope}</b>. Removing the override sends {meritScope} back to the default.</>
                          : <>Editing the <b>default</b> — any class without an override uses these settings.</>}
                      </Typography>
                    </Stack>

                    {meritOverrides.length > 0 && (
                      <Box sx={{ mt: 1.2, pt: 1.2, borderTop: '1px solid', borderColor: 'divider' }}>
                        <Typography variant="caption" sx={{ fontWeight: 800, textTransform: 'uppercase', letterSpacing: 0.6, color: 'text.secondary', display: 'block', mb: 0.6 }}>
                          Class overrides in effect
                        </Typography>
                        <Stack direction="row" flexWrap="wrap" gap={0.6}>
                          {meritOverrides.map(o => (
                            <Chip key={o.cla} size="small"
                              label={o.cla}
                              color={meritScope === o.cla ? 'warning' : 'default'}
                              variant={meritScope === o.cla ? 'filled' : 'outlined'}
                              onClick={() => switchMeritScope(o.cla)}
                              onDelete={() => removeMeritOverride(o.cla)}
                              sx={{ fontWeight: 700 }} />
                          ))}
                        </Stack>
                      </Box>
                    )}
                  </Box>

                  {/* Two switches side-by-side */}
                  <Grid container spacing={1.5}>
                    <Grid item xs={12} md={6}>
                      <Box sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1.5, p: 1.5, height: '100%' }}>
                        <FormControlLabel
                          control={<Switch checked={merit.show_position}
                            onChange={e => setMerit(m => ({ ...m, show_position: e.target.checked }))} />}
                          label={<Typography sx={{ fontWeight: 800, fontSize: 14 }}>Show Position in Class</Typography>}
                        />
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                          Print 1st, 2nd, 3rd … on report cards based on total marks. Turn off if your school
                          doesn't share positions with parents.
                        </Typography>
                      </Box>
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <Box sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1.5, p: 1.5, height: '100%' }}>
                        <FormControlLabel
                          control={<Switch checked={merit.show_grading}
                            onChange={e => setMerit(m => ({ ...m, show_grading: e.target.checked }))} />}
                          label={<Typography sx={{ fontWeight: 800, fontSize: 14 }}>Show Examination Grading (Aggregate)</Typography>}
                        />
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                          Compute the aggregate ("Grade 06" style) from top N core + top M elective subjects.
                        </Typography>
                      </Box>
                    </Grid>
                  </Grid>

                  {/* Grading configuration — only visible when grading is on */}
                  {merit.show_grading && (
                    <Box sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1.5, p: 2, bgcolor: 'grey.50' }}>
                      <Typography sx={{ fontWeight: 800, fontSize: 13, mb: 1.5, textTransform: 'uppercase', letterSpacing: 0.6, color: 'text.secondary' }}>
                        Aggregate rules
                      </Typography>
                      <Grid container spacing={1.5}>
                        <Grid item xs={12} sm={6}>
                          <TextField fullWidth size="small" type="number" label="Core subjects to count"
                            value={merit.core_count}
                            onChange={e => setMerit(m => ({ ...m, core_count: Math.max(0, Number(e.target.value) || 0) }))}
                            helperText="e.g. 4 — the top 4 core subjects contribute to the aggregate"
                            inputProps={{ min: 0 }} />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                          <TextField fullWidth size="small" type="number" label="Elective subjects to count"
                            value={merit.elective_count}
                            onChange={e => setMerit(m => ({ ...m, elective_count: Math.max(0, Number(e.target.value) || 0) }))}
                            helperText="e.g. 2 — the top 2 elective subjects contribute"
                            inputProps={{ min: 0 }} />
                        </Grid>
                        <Grid item xs={12}>
                          <Autocomplete
                            multiple options={meritSubjects}
                            value={merit.compulsory_subjects}
                            onChange={(_, v) => setMerit(m => ({ ...m, compulsory_subjects: v }))}
                            renderTags={(value, getTagProps) =>
                              value.map((opt, i) => (
                                <Chip key={opt} label={opt} size="small" color="warning"
                                  sx={{ fontWeight: 700 }} {...getTagProps({ index: i })} />
                              ))}
                            renderInput={(params) => (
                              <TextField {...params} size="small" label="Compulsory subjects"
                                helperText={meritSubjects.length === 0
                                  ? `No subjects registered yet for ${scopeYear} — add them under Register Subject first.`
                                  : 'Must-count subjects (e.g. Core Mathematics, English) — always included in the aggregate.'} />
                            )} />
                        </Grid>
                        <Grid item xs={12}>
                          <Typography variant="caption" color="text.secondary" component="div">
                            <b>How the aggregate is worked out</b> — total slots ={' '}
                            <b>{(merit.core_count || 0) + (merit.elective_count || 0)}</b>
                            &nbsp;({merit.core_count || 0} core + {merit.elective_count || 0} elective).
                            Best possible = <b>Grade 0{(merit.core_count || 0) + (merit.elective_count || 0)}</b>{' '}
                            (A1 in every counted subject).
                            <Box component="ul" sx={{ m: 0.5, pl: 2.5 }}>
                              <li>
                                <b>No compulsory subjects set</b> — take the student's {merit.core_count || 0} best-graded
                                core subjects and {merit.elective_count || 0} best-graded elective subjects.
                              </li>
                              <li>
                                <b>Compulsory subjects set</b> — those are locked in first, then the remaining
                                slots are filled with the student's next best core / elective grades.
                              </li>
                            </Box>
                          </Typography>
                        </Grid>
                      </Grid>
                    </Box>
                  )}

                  <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                    <Button variant="contained" color="warning" startIcon={<SaveIcon />}
                      onClick={saveMerit} disabled={meritSaving || meritLoading}
                      sx={{ textTransform: 'none', fontWeight: 800 }}>
                      {meritSaving
                        ? 'Saving…'
                        : (meritScope ? `Save settings for ${meritScope}` : 'Save default settings')}
                    </Button>
                  </Box>
                </Stack>
              )}
            </AccordionDetails>
          </Accordion>

          {/* ─────────────── 6. Teacher Assignments ─────────────── */}
          <Accordion expanded={expanded === 'teachers'} onChange={togglePanel('teachers')}
            elevation={0} sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 2, '&:before': { display: 'none' } }}>
            <AccordionSummary expandIcon={<ExpandIcon />} sx={{ '& .MuiAccordionSummary-content': { my: 1.5 } }}>
              <SectionTitle
                icon={<TeacherAssignIcon />}
                color="warning"
                title="Teacher Assignments"
                subtitle="Map each teacher account to the specific Class × Subject they own for an academic year — non-admin teachers only see their own subjects in the Score Sheet and Subject Registration pages"
              />
            </AccordionSummary>
            <AccordionDetails>
              <TeacherAssignmentsPanel scopeYear={scopeYear} scopeTerm={scopeTerm} />
            </AccordionDetails>
          </Accordion>

          {/* ─────────────── 7. Teacher Portal Preview ─────────────── */}
          <Accordion expanded={expanded === 'preview'} onChange={togglePanel('preview')}
            elevation={0} sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 2, '&:before': { display: 'none' } }}>
            <AccordionSummary expandIcon={<ExpandIcon />} sx={{ '& .MuiAccordionSummary-content': { my: 1.5 } }}>
              <SectionTitle
                icon={<PreviewIcon />}
                color="info"
                title="Teacher Portal Preview"
                subtitle="Readonly view of what each teacher sees — assigned subjects, score-entry progress, and the merit rules that apply to their classes"
                preview={previewPreview}
              />
            </AccordionSummary>
            <AccordionDetails>
              {!scopeYear ? (
                <Alert severity="warning" variant="outlined">
                  Pick a year in the <b>Settings Scope</b> banner above first — the preview needs a year.
                </Alert>
              ) : (
                <Stack spacing={1.5}>
                  <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={1}>
                    <Alert severity="info" variant="outlined" sx={{ flex: 1, mr: 1 }}>
                      Showing <b>{scopeYear}</b>{activityTerm ? <> · <b>{activityTerm.replace('_', ' ')}</b></> : ''}.
                      Progress = students with at least one score saved for that subject × exam state, out of the class headcount.
                    </Alert>
                    <Button size="small" variant="outlined" startIcon={<RefreshIcon />}
                      onClick={loadTeacherActivity} disabled={activityLoading}
                      sx={{ textTransform: 'none', fontWeight: 700 }}>
                      {activityLoading ? 'Loading…' : 'Refresh'}
                    </Button>
                  </Stack>

                  {activityLoading && (
                    <Box sx={{ py: 4, textAlign: 'center' }}><CircularProgress /></Box>
                  )}

                  {!activityLoading && teachersActivity.length === 0 && (
                    <Alert severity="info">
                      No teachers assigned for {scopeYear} yet — add them under <b>Teacher Assignments</b> above.
                    </Alert>
                  )}

                  {!activityLoading && teachersActivity.map(t => {
                    const totals = (t.assignments || []).reduce((acc, a) => {
                      for (const s of a.states || []) { acc.e += s.entered || 0; acc.n += s.total || 0; }
                      return acc;
                    }, { e: 0, n: 0 });
                    const overallPct = totals.n > 0 ? Math.round((totals.e / totals.n) * 100) : 0;
                    const isOpen = expandedTeacher === t.user_id;
                    return (
                      <Box key={t.user_id}
                        sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1.5, overflow: 'hidden' }}>
                        <Stack direction="row" alignItems="center" spacing={1.5}
                          sx={{ px: 1.5, py: 1, bgcolor: 'grey.50',
                                '&:hover': { bgcolor: 'grey.100' } }}>
                          <Avatar sx={{ bgcolor: 'primary.main', width: 34, height: 34, fontSize: 13, fontWeight: 800 }}>
                            {(t.teacher_name || '?').split(' ').map(s => s[0]).slice(0, 2).join('').toUpperCase()}
                          </Avatar>
                          <Box sx={{ flex: 1, minWidth: 0, cursor: 'pointer' }}
                            onClick={() => setExpandedTeacher(isOpen ? null : t.user_id)}>
                            <Typography sx={{ fontWeight: 800, fontSize: 14, lineHeight: 1.2 }}>
                              {t.teacher_name}
                            </Typography>
                            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', lineHeight: 1.2 }}>
                              {t.teacher_email || '—'} · {(t.assignments || []).length} subject{t.assignments.length === 1 ? '' : 's'}
                            </Typography>
                          </Box>
                          <Box sx={{ minWidth: 140, display: { xs: 'none', sm: 'block' }, cursor: 'pointer' }}
                            onClick={() => setExpandedTeacher(isOpen ? null : t.user_id)}>
                            <Stack direction="row" alignItems="center" spacing={0.8}>
                              <Box sx={{ flex: 1 }}>
                                <LinearProgress variant="determinate" value={overallPct}
                                  color={overallPct >= 75 ? 'success' : overallPct >= 40 ? 'warning' : 'inherit'}
                                  sx={{ height: 6, borderRadius: 3 }} />
                              </Box>
                              <Typography sx={{ fontWeight: 800, fontSize: 12, minWidth: 34, textAlign: 'right' }}>
                                {overallPct}%
                              </Typography>
                            </Stack>
                          </Box>
                          <Tooltip title={`Open ${t.teacher_name}'s portal (view as)`}>
                            <Button size="small" variant="contained" color="info"
                              startIcon={<LaunchIcon sx={{ fontSize: 16 }} />}
                              onClick={(e) => {
                                e.stopPropagation();
                                navigate(`/teacher-portal?view_as=${t.user_id}`);
                              }}
                              sx={{ textTransform: 'none', fontWeight: 700, whiteSpace: 'nowrap' }}>
                              Open portal
                            </Button>
                          </Tooltip>
                          <ExpandIcon
                            onClick={() => setExpandedTeacher(isOpen ? null : t.user_id)}
                            sx={{
                              cursor: 'pointer',
                              transform: isOpen ? 'rotate(180deg)' : 'none',
                              transition: 'transform 0.2s', color: 'action.active',
                            }} />
                        </Stack>
                        {isOpen && (
                          <TableContainer>
                            <Table size="small" sx={COMPACT_TABLE}>
                              <TableHead>
                                <TableRow>
                                  <TableCell sx={{ width: 100 }}>Class</TableCell>
                                  <TableCell>Subject</TableCell>
                                  <TableCell sx={{ minWidth: 260 }}>Score entry progress</TableCell>
                                  <TableCell sx={{ width: 200 }}>Rules applied</TableCell>
                                </TableRow>
                              </TableHead>
                              <TableBody>
                                {(t.assignments || []).length === 0 && (
                                  <TableRow>
                                    <TableCell colSpan={4} align="center"
                                      sx={{ py: 2, color: 'text.secondary', fontStyle: 'italic' }}>
                                      No assignments for this teacher.
                                    </TableCell>
                                  </TableRow>
                                )}
                                {(t.assignments || []).map((a, ai) => (
                                  <TableRow key={`${a.cla}-${a.sub}-${ai}`} hover>
                                    <TableCell sx={{ fontWeight: 700 }}>{a.cla}</TableCell>
                                    <TableCell>{a.sub}</TableCell>
                                    <TableCell>
                                      {(a.states || []).length === 0 ? (
                                        <Typography variant="caption" color="text.disabled" fontStyle="italic">
                                          No exam states registered
                                        </Typography>
                                      ) : (
                                        <Stack spacing={0.5}>
                                          {a.states.map(s => (
                                            <Box key={s.state}>
                                              <Stack direction="row" alignItems="center" spacing={0.8}
                                                sx={{ mb: 0.2 }}>
                                                <Chip size="small" label={s.state} sx={COMPACT_CHIP} />
                                                <Typography variant="caption" color="text.secondary" sx={{ flex: 1 }}>
                                                  {s.entered} / {s.total} students
                                                </Typography>
                                                <Typography sx={{ fontWeight: 800, fontSize: 11 }}>
                                                  {s.pct}%
                                                </Typography>
                                              </Stack>
                                              <LinearProgress variant="determinate" value={s.pct}
                                                color={s.pct >= 75 ? 'success' : s.pct >= 40 ? 'warning' : 'inherit'}
                                                sx={{ height: 4, borderRadius: 2 }} />
                                            </Box>
                                          ))}
                                        </Stack>
                                      )}
                                    </TableCell>
                                    <TableCell>
                                      <Stack spacing={0.4}>
                                        <Chip size="small"
                                          color={a.rule?.show_position ? 'success' : 'default'}
                                          variant={a.rule?.show_position ? 'filled' : 'outlined'}
                                          label={a.rule?.show_position ? 'Position: on' : 'Position: off'}
                                          sx={COMPACT_CHIP} />
                                        <Chip size="small"
                                          color={a.rule?.show_grading ? 'success' : 'default'}
                                          variant={a.rule?.show_grading ? 'filled' : 'outlined'}
                                          label={a.rule?.show_grading
                                            ? `Grading: ${a.rule.core_count}+${a.rule.elective_count}`
                                            : 'Grading: off'}
                                          sx={COMPACT_CHIP} />
                                        {a.rule?.scope === 'override' && (
                                          <Chip size="small" color="warning" variant="outlined"
                                            label="Class override" sx={COMPACT_CHIP} />
                                        )}
                                      </Stack>
                                    </TableCell>
                                  </TableRow>
                                ))}
                              </TableBody>
                            </Table>
                          </TableContainer>
                        )}
                      </Box>
                    );
                  })}
                </Stack>
              )}
            </AccordionDetails>
          </Accordion>
        </Stack>
      )}

      {/* SBA component editor dialog — commits to the draft in memory.
          Actual persistence happens when the operator clicks "Save changes"
          on the profile row. */}
      <Dialog open={sbaOpen} onClose={() => setSbaOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ fontWeight: 800 }}>
          {sbaEditing ? 'Edit SBA component' : 'Add SBA component'}
        </DialogTitle>
        <DialogContent dividers>
          {sbaError && <Alert severity="error" sx={{ mb: 1.5 }}>{sbaError}</Alert>}
          <Grid container spacing={1.2}>
            <Grid item xs={12}>
              <TextField select fullWidth size="small" label="Component label"
                value={sbaForm.label}
                onChange={e => setSbaF(f => ({ ...f, label: e.target.value }))}
                helperText="Pick a standard SBA label or type a custom one">
                {['Assignment', 'Project', 'Class Exercise', 'Quiz / Test', 'End of Term Exam', 'Other'].map(o => (
                  <MenuItem key={o} value={o}>{o}</MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={6}>
              <TextField fullWidth size="small" type="number" label="Items per student"
                value={sbaForm.item_count}
                onChange={e => setSbaF(f => ({ ...f, item_count: e.target.value }))}
                helperText="How many the teacher records (e.g. 3 assignments)" />
            </Grid>
            <Grid item xs={6}>
              <TextField fullWidth size="small" type="number" label="Weight (%)"
                value={sbaForm.weight_pct}
                onChange={e => setSbaF(f => ({ ...f, weight_pct: e.target.value }))}
                helperText="Slice of the overall mark" />
            </Grid>
            <Grid item xs={6}>
              <TextField fullWidth size="small" type="number" label="Max per item"
                value={sbaForm.max_mark_per_item ?? 100}
                onChange={e => setSbaF(f => ({ ...f, max_mark_per_item: e.target.value }))}
                helperText="Denominator for each entry (e.g. 10, 20, 100)" />
            </Grid>
            <Grid item xs={6}>
              <TextField fullWidth size="small" type="number" label="Sort order"
                value={sbaForm.sort_order}
                onChange={e => setSbaF(f => ({ ...f, sort_order: e.target.value }))} />
            </Grid>
            <Grid item xs={12} sx={{ display: 'flex', alignItems: 'center' }}>
              <FormControlLabel control={
                <Switch checked={!!sbaForm.is_exam}
                  onChange={e => setSbaF(f => ({ ...f, is_exam: e.target.checked }))} />
              } label="End-of-Term Exam" />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSbaOpen(false)} sx={{ textTransform: 'none' }}>Cancel</Button>
          <Button variant="contained" color="secondary" onClick={commitSba}
            sx={{ textTransform: 'none', fontWeight: 800 }}>
            {sbaEditing ? 'Update' : 'Add'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Remark catalogue editor dialog */}
      <Dialog open={remarkOpen} onClose={() => setRemarkOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ fontWeight: 800 }}>
          {remarkEditing ? 'Edit remark' : 'Add remark'}
        </DialogTitle>
        <DialogContent dividers>
          {remarkError && <Alert severity="error" sx={{ mb: 1.5 }}>{remarkError}</Alert>}
          <Grid container spacing={1.2}>
            <Grid item xs={12} sm={5}>
              <TextField select fullWidth size="small" label="Category"
                value={remarkForm.cat}
                onChange={e => setRemarkF(f => ({ ...f, cat: e.target.value }))}>
                {REMARK_CATEGORIES.map(({ cat, label }) => (
                  <MenuItem key={cat} value={cat}>{label}</MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={12} sm={7}>
              <TextField fullWidth size="small" label="Remark text" multiline maxRows={3}
                value={remarkForm.name}
                onChange={e => setRemarkF(f => ({ ...f, name: e.target.value }))}
                autoFocus />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRemarkOpen(false)} sx={{ textTransform: 'none' }}>Cancel</Button>
          <Button variant="contained" onClick={saveRemark} disabled={remarkSaving}
            sx={{ textTransform: 'none', fontWeight: 800 }}>
            {remarkSaving ? 'Saving…' : (remarkEditing ? 'Save changes' : 'Add')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Mock / Mid-Term grading ladder.
          Edited as a whole table rather than one band at a time: a
          grading scale only means anything as a complete ladder, and
          saving a band at a time can leave a gap that silently grades
          nobody. */}
      <Dialog open={etBandOpen} onClose={() => setEtBandOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle sx={{ fontWeight: 800 }}>
          {etBandState} grading scale
        </DialogTitle>
        <DialogContent dividers>
          {etBandErr && <Alert severity="error" sx={{ mb: 1.5 }}>{etBandErr}</Alert>}
          <Alert severity="info" variant="outlined" sx={{ mb: 1.5 }}>
            Bands used for <b>{etBandState}</b> only. Started as a copy of the school
            scale — change what differs. Ranges must not overlap.
          </Alert>
          <TableContainer sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
            <Table size="small" sx={COMPACT_TABLE}>
              <TableHead>
                <TableRow>
                  <TableCell align="right" sx={{ width: 90 }}>From</TableCell>
                  <TableCell align="right" sx={{ width: 90 }}>To</TableCell>
                  <TableCell sx={{ width: 110 }}>Grade</TableCell>
                  <TableCell>Remarks</TableCell>
                  <TableCell align="right" sx={{ width: 50 }} />
                </TableRow>
              </TableHead>
              <TableBody>
                {etBandRows.map((b, i) => (
                  <TableRow key={i}>
                    <TableCell align="right">
                      <TextField size="small" type="number" value={b.marks_1 ?? ''}
                        onChange={e => setEtBandRows(rs => rs.map((x, j) =>
                          j === i ? { ...x, marks_1: e.target.value } : x))}
                        inputProps={{ style: { textAlign: 'right', padding: 5 } }} />
                    </TableCell>
                    <TableCell align="right">
                      <TextField size="small" type="number" value={b.marks_2 ?? ''}
                        onChange={e => setEtBandRows(rs => rs.map((x, j) =>
                          j === i ? { ...x, marks_2: e.target.value } : x))}
                        inputProps={{ style: { textAlign: 'right', padding: 5 } }} />
                    </TableCell>
                    <TableCell>
                      <TextField size="small" value={b.grade ?? ''}
                        onChange={e => setEtBandRows(rs => rs.map((x, j) =>
                          j === i ? { ...x, grade: e.target.value } : x))}
                        inputProps={{ style: { padding: 5 } }} />
                    </TableCell>
                    <TableCell>
                      <TextField size="small" fullWidth value={b.remarks ?? ''}
                        onChange={e => setEtBandRows(rs => rs.map((x, j) =>
                          j === i ? { ...x, remarks: e.target.value } : x))}
                        inputProps={{ style: { padding: 5 } }} />
                    </TableCell>
                    <TableCell align="right">
                      <Tooltip title="Remove band">
                        <IconButton color="error"
                          onClick={() => setEtBandRows(rs => rs.filter((_, j) => j !== i))}>
                          <DeleteIcon sx={{ fontSize: 16 }} />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
          <Button size="small" startIcon={<AddIcon />} sx={{ mt: 1, textTransform: 'none', fontWeight: 800 }}
            onClick={() => setEtBandRows(rs => [...rs, { marks_1: '', marks_2: '', grade: '', remarks: '' }])}>
            Add band
          </Button>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEtBandOpen(false)} sx={{ textTransform: 'none', fontWeight: 700 }}>
            Cancel
          </Button>
          <Button variant="contained" color="secondary" onClick={saveExamBands}
            disabled={!!etSaving}
            startIcon={etSaving ? <CircularProgress size={13} color="inherit" /> : <SaveIcon />}
            sx={{ textTransform: 'none', fontWeight: 800 }}>
            Save scale
          </Button>
        </DialogActions>
      </Dialog>

      {/* Grade band editor dialog */}
      <Dialog open={bandOpen} onClose={() => setBO(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ fontWeight: 800 }}>
          {bandEditing ? 'Edit grade band' : 'Add grade band'}
        </DialogTitle>
        <DialogContent dividers>
          {bandError && <Alert severity="error" sx={{ mb: 1.5 }}>{bandError}</Alert>}
          <Grid container spacing={1.2}>
            <Grid item xs={6}>
              <TextField fullWidth size="small" type="number" label="Marks from"
                value={bandForm.marks_1}
                onChange={e => setBF(f => ({ ...f, marks_1: e.target.value }))} />
            </Grid>
            <Grid item xs={6}>
              <TextField fullWidth size="small" type="number" label="Marks to"
                value={bandForm.marks_2}
                onChange={e => setBF(f => ({ ...f, marks_2: e.target.value }))} />
            </Grid>
            <Grid item xs={6}>
              <TextField fullWidth size="small" label="Grade (e.g. A1)"
                value={bandForm.grade}
                onChange={e => setBF(f => ({ ...f, grade: e.target.value }))} />
            </Grid>
            <Grid item xs={6}>
              <TextField fullWidth size="small" label="Remarks (e.g. Excellent)"
                value={bandForm.remarks}
                onChange={e => setBF(f => ({ ...f, remarks: e.target.value }))} />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setBO(false)} sx={{ textTransform: 'none' }}>Cancel</Button>
          <Button variant="contained" onClick={saveBand} disabled={bandSaving}
            sx={{ textTransform: 'none', fontWeight: 800 }}>
            {bandSaving ? 'Saving…' : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
